import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress
} from '@mui/material'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { storeAPI } from '@services/tanoak-api'
import { StoreComparisonSkeleton } from '@components/LoadingSkeletons'

interface StoreMetrics {
  store: string
  storeId: string
  revenue: number
  grossProfit: number
  laborEfficiency: number
  partsMargin: number
  customerSatisfaction: number
  technicianUtilization: number
  avgTicket: number
  repeatCustomers: number
}

const StoreComparison: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('week')
  const [loading, setLoading] = useState(true)
  const [storeData, setStoreData] = useState<StoreMetrics[]>([
    {
      store: 'Woodway',
      storeId: 'TX062',
      revenue: 156125,
      grossProfit: 52041.74 * 0.55, // 55% of total
      laborEfficiency: 82,
      partsMargin: 35,
      customerSatisfaction: 92,
      technicianUtilization: 78,
      avgTicket: 485,
      repeatCustomers: 68
    },
    {
      store: 'Temple',
      storeId: 'TX096',
      revenue: 127750,
      grossProfit: 52041.74 * 0.45, // 45% of total
      laborEfficiency: 75,
      partsMargin: 33,
      customerSatisfaction: 89,
      technicianUtilization: 72,
      avgTicket: 425,
      repeatCustomers: 62
    }
  ])

  useEffect(() => {
    fetchStoreData()
  }, [timeRange])

  const fetchStoreData = async () => {
    setLoading(true)
    try {
      const data = await storeAPI.getComparison(timeRange)
      if (data?.stores && data.stores.length > 0) {
        setStoreData(data.stores.map((store: any) => ({
          store: store.name,
          storeId: store.id,
          revenue: store.revenue,
          grossProfit: store.gross_profit,
          laborEfficiency: store.labor_efficiency || 75,
          partsMargin: store.parts_margin || 33,
          customerSatisfaction: store.customer_satisfaction || 90,
          technicianUtilization: store.technician_utilization || 75,
          avgTicket: store.avg_ticket || 450,
          repeatCustomers: store.repeat_customers || 65
        })))
      }
    } catch (error) {
      console.error('Failed to fetch store comparison:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <StoreComparisonSkeleton />
  }

  const pieData = [
    { name: 'Woodway', value: storeData[0].revenue, color: '#8884d8' },
    { name: 'Temple', value: storeData[1].revenue, color: '#82ca9d' }
  ]

  const radarData = [
    {
      metric: 'Labor Efficiency',
      woodway: storeData[0].laborEfficiency,
      temple: storeData[1].laborEfficiency,
      fullMark: 100
    },
    {
      metric: 'Parts Margin',
      woodway: storeData[0].partsMargin,
      temple: storeData[1].partsMargin,
      fullMark: 50
    },
    {
      metric: 'Customer Satisfaction',
      woodway: storeData[0].customerSatisfaction,
      temple: storeData[1].customerSatisfaction,
      fullMark: 100
    },
    {
      metric: 'Tech Utilization',
      woodway: storeData[0].technicianUtilization,
      temple: storeData[1].technicianUtilization,
      fullMark: 100
    },
    {
      metric: 'Repeat Customers',
      woodway: storeData[0].repeatCustomers,
      temple: storeData[1].repeatCustomers,
      fullMark: 100
    }
  ]

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const calculateDifference = (woodway: number, temple: number) => {
    const diff = ((woodway - temple) / temple) * 100
    return {
      value: diff,
      formatted: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`,
      isPositive: diff >= 0
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Store Comparison
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, value) => value && setTimeRange(value)}
          size="small"
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="quarter">Quarter</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Revenue Split */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => `${entry.name}: ${((entry.value / (storeData[0].revenue + storeData[1].revenue)) * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Woodway (TX062)
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Revenue</Typography>
                <Typography variant="body2">{formatCurrency(storeData[0].revenue)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Gross Profit</Typography>
                <Typography variant="body2">{formatCurrency(storeData[0].grossProfit)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Avg Ticket</Typography>
                <Typography variant="body2">{formatCurrency(storeData[0].avgTicket)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Margin</Typography>
                <Typography variant="body2">{storeData[0].partsMargin}%</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Temple (TX096)
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Revenue</Typography>
                <Typography variant="body2">{formatCurrency(storeData[1].revenue)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Gross Profit</Typography>
                <Typography variant="body2">{formatCurrency(storeData[1].grossProfit)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Avg Ticket</Typography>
                <Typography variant="body2">{formatCurrency(storeData[1].avgTicket)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Margin</Typography>
                <Typography variant="body2">{storeData[1].partsMargin}%</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Comparison */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Metrics Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Woodway" dataKey="woodway" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  <Radar name="Temple" dataKey="temple" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Performance Indicators
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="center">Woodway</TableCell>
                      <TableCell align="center">Temple</TableCell>
                      <TableCell align="center">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Labor Efficiency</TableCell>
                      <TableCell align="center">{storeData[0].laborEfficiency}%</TableCell>
                      <TableCell align="center">{storeData[1].laborEfficiency}%</TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          {calculateDifference(storeData[0].laborEfficiency, storeData[1].laborEfficiency).isPositive ? (
                            <TrendingUpIcon fontSize="small" color="success" />
                          ) : (
                            <TrendingDownIcon fontSize="small" color="error" />
                          )}
                          <Typography variant="body2" color={calculateDifference(storeData[0].laborEfficiency, storeData[1].laborEfficiency).isPositive ? 'success.main' : 'error.main'}>
                            {calculateDifference(storeData[0].laborEfficiency, storeData[1].laborEfficiency).formatted}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Tech Utilization</TableCell>
                      <TableCell align="center">{storeData[0].technicianUtilization}%</TableCell>
                      <TableCell align="center">{storeData[1].technicianUtilization}%</TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <TrendingUpIcon fontSize="small" color="success" />
                          <Typography variant="body2" color="success.main">
                            {calculateDifference(storeData[0].technicianUtilization, storeData[1].technicianUtilization).formatted}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Customer Satisfaction</TableCell>
                      <TableCell align="center">{storeData[0].customerSatisfaction}%</TableCell>
                      <TableCell align="center">{storeData[1].customerSatisfaction}%</TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <TrendingUpIcon fontSize="small" color="success" />
                          <Typography variant="body2" color="success.main">
                            {calculateDifference(storeData[0].customerSatisfaction, storeData[1].customerSatisfaction).formatted}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Improvement Opportunities */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Improvement Opportunities
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Woodway (TX062)
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" gutterBottom>
                  Tech Utilization: Currently at 78%
                </Typography>
                <LinearProgress variant="determinate" value={78} />
                <Typography variant="caption" color="textSecondary">
                  Opportunity: Schedule optimization could improve by 7%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Temple (TX096)
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" gutterBottom>
                  Labor Efficiency: Currently at 75%
                </Typography>
                <LinearProgress variant="determinate" value={75} />
                <Typography variant="caption" color="textSecondary">
                  Opportunity: Training program could improve by 10%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default StoreComparison